const NON_PRODUCTION_HOSTNAME_HASHES = new Set([
	'456a2c43c890144b9481cce84171af5e2cdbf5733047f411a4f31d6c80b5b182', // staging
	'e27d26f1c74b73280fa3e8d9b5673888d92f15daada649bb560708889dc42247', // dev
	'49960de5880e8c687434170f6476605b8fe4aeb9a28632c7995cf3ba831d9763', // localhost
]);

async function hashHostname(hostname: string): Promise<string> {
	const data = new TextEncoder().encode(hostname);
	const buffer = await crypto.subtle.digest('SHA-256', data);
	return Array.from(new Uint8Array(buffer))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
}

export async function resolvePosthogToken(
	productionToken: string | undefined,
	stagingToken: string | undefined,
): Promise<string | undefined> {
	if (!productionToken) return undefined;
	if (!stagingToken) return productionToken;
	const hash = await hashHostname(window.location.hostname);
	return NON_PRODUCTION_HOSTNAME_HASHES.has(hash) ? stagingToken : productionToken;
}

type PostHogLike = {
	capture?: (event: string, properties?: Record<string, unknown>) => void;
	identify?: (id: string, properties?: Record<string, unknown>) => void;
	group?: (type: string, id: string, properties?: Record<string, unknown>) => void;
	register?: (properties: Record<string, unknown>) => void;
	reset?: () => void;
};

function getPostHog(): PostHogLike | null {
	try {
		if (typeof window !== 'undefined') {
			const w = window as unknown as { posthog?: PostHogLike };
			if (w.posthog) return w.posthog;
		}
	} catch {
		/* no-op */
	}
	return null;
}

type AnalyticsUser = {
	id?: string;
	email?: string | null;
	firstName?: string | null;
	lastName?: string | null;
} | null | undefined;

type FieldChangeContext = {
	sectionIndex: number;
	sectionType: string;
	sectionName: string;
};

type SectionNavigateContext = {
	sectionType?: string;
	sectionName?: string;
	totalSections?: number;
	errorCount?: number;
	errorFields?: string[];
};

type SentryLike = {
	captureException?: (err: unknown, context?: { extra?: Record<string, unknown>; tags?: Record<string, string> }) => void;
	captureMessage?: (message: string, level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug') => void;
};

function getSentry(): SentryLike | null {
	try {
		if (typeof window !== 'undefined' && (window as unknown as { Sentry?: SentryLike }).Sentry)
			return (window as unknown as { Sentry: SentryLike }).Sentry;
	} catch {
		/* no-op */
	}
	return null;
}

export function captureError(err: unknown, context?: { extra?: Record<string, unknown>; tags?: Record<string, string> }) {
	try {
		const sentry = getSentry();
		if (sentry?.captureException) {
			sentry.captureException(err, context);
		} else {
			console.error('[captureError]', err, context);
		}
	} catch (innerErr) {
		console.error('[captureError] failed to report', { err, innerErr });
	}
}

export const analytics = {
	capture(event: string, properties?: Record<string, unknown>) {
		try {
			getPostHog()?.capture?.(event, properties);
		} catch (err) {
			console.error('[analytics] capture error', { event, err });
		}
	},
	identify(id: string, traits?: Record<string, unknown>) {
		try {
			getPostHog()?.identify?.(id, traits);
		} catch (err) {
			console.error('[analytics] identify error', { id, err });
		}
	},
	group(type: string, id: string, props?: Record<string, unknown>) {
		try {
			getPostHog()?.group?.(type, id, props);
		} catch (err) {
			console.error('[analytics] group error', { type, id, err });
		}
	},
	register(props: Record<string, unknown>) {
		try {
			getPostHog()?.register?.(props);
		} catch (err) {
			console.error('[analytics] register error', { props, err });
		}
	},
	reset() {
		try {
			getPostHog()?.reset?.();
		} catch (err) {
			console.error('[analytics] reset error', err);
		}
	},
};

export function identifyActor(actor: AnalyticsUser) {
	if (!actor?.id) return;
	const name = [actor.firstName, actor.lastName].filter(Boolean).join(' ');
	analytics.identify(actor.id, { email: actor.email, name });
	analytics.register({
		actor_id: actor.id,
		actor_name: name,
		actor_email: actor.email,
	});
}

export function taskUserProps(user: AnalyticsUser): Record<string, unknown> {
	if (!user?.id) return {};
	return {
		task_user_id: user.id,
		task_user_name: [user.firstName, user.lastName].filter(Boolean).join(' '),
		task_user_email: user.email,
	};
}

export function createFieldChangeHandler(taskProps: Record<string, unknown>) {
	return (fieldName: string, _fieldValue: unknown, ctx: FieldChangeContext) => {
		analytics.capture('task_field_changed', {
			...taskProps,
			field_name: fieldName,
			section_index: ctx.sectionIndex,
			section_type: ctx.sectionType,
			section_name: ctx.sectionName,
		});
	};
}

export function createSectionNavigateHandler(taskProps: Record<string, unknown>) {
	return (direction: string, from: number, to: number | null, ctx: SectionNavigateContext) => {
		const sectionCtx = {
			...taskProps,
			section_index: from,
			section_type: ctx.sectionType,
			section_name: ctx.sectionName,
			total_sections: ctx.totalSections,
		};
		if (direction === 'started') {
			analytics.capture('task_section_started', sectionCtx);
			return;
		}
		analytics.capture('task_section_navigated', {
			...sectionCtx,
			direction,
			from_section: from,
			to_section: to,
			...(ctx.errorFields && { error_count: ctx.errorCount, error_fields: ctx.errorFields }),
		});
		if (['next', 'previous', 'submit'].includes(direction)) {
			analytics.capture('task_section_completed', sectionCtx);
		}
	};
}
